import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useMyContactRequests, useContactRequestsByDeal, useAcceptContactRequest, useRejectContactRequest, useAllContactRequests, useReceivedContactRequests } from '@/hooks/api/useContactRequests';
import { ContactRequest } from '@/services/api/contact-requests.api';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle, XCircle, Clock, Send, Loader2, ExternalLink, Users, MessageCircle } from 'lucide-react';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DealCommunicationHistory } from '@/components/deal-room/DealCommunicationHistory';

const STATUS_CONFIG = {
  pending: { label: 'Pending', labelFr: 'En attente', icon: Clock, variant: 'secondary' as const, color: 'text-yellow-600' },
  accepted: { label: 'Accepted', labelFr: 'Acceptée', icon: CheckCircle, variant: 'default' as const, color: 'text-green-600' },
  rejected: { label: 'Rejected', labelFr: 'Refusée', icon: XCircle, variant: 'destructive' as const, color: 'text-red-600' },
};

/** Buyer view — shows their own contact requests */
export function BuyerContactRequests({ language = 'en' }: { language?: string }) {
  const { data: requests = [], isLoading } = useMyContactRequests();
  const t = language === 'fr';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="h-5 w-5" />
          {t ? 'Mes demandes de contact' : 'My Contact Requests'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : requests.length === 0 ? (
          <p className="text-center text-muted-foreground py-6">{t ? 'Aucune demande envoyée' : 'No requests sent yet'}</p>
        ) : (
          <div className="space-y-4">
            {requests.map((r) => (
              <div key={r.id} className="space-y-2">
                <RequestRow request={r} language={language} />
                {r.status === 'accepted' && (
                  <DealCommunicationHistory contactRequest={r} language={language} />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Seller view — shows incoming requests via NestJS API */
export function SellerContactRequests({ language = 'en' }: { language?: string }) {
  const { data: requests = [], isLoading } = useReceivedContactRequests();
  const acceptMutation = useAcceptContactRequest();
  const rejectMutation = useRejectContactRequest();
  const { toast } = useToast();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const t = language === 'fr';

  const handleAccept = async (id: string) => {
    try {
      await acceptMutation.mutateAsync(id);
      toast({ title: t ? 'Demande acceptée' : 'Request accepted' });
    } catch {
      toast({ title: t ? 'Erreur' : 'Error', variant: 'destructive' });
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectMutation.mutateAsync({ id, reason: rejectReason });
      setRejectingId(null);
      setRejectReason('');
      toast({ title: t ? 'Demande refusée' : 'Request rejected' });
    } catch {
      toast({ title: t ? 'Erreur' : 'Error', variant: 'destructive' });
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="h-5 w-5" />
          {t ? 'Demandes de contact reçues' : 'Incoming Contact Requests'}
          {pendingCount > 0 && (
            <Badge variant="destructive" className="ml-2">{pendingCount}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : requests.length === 0 ? (
          <p className="text-center text-muted-foreground py-6">{t ? 'Aucune demande reçue' : 'No requests received'}</p>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="border rounded-lg p-4 space-y-3">
                <RequestRow request={r} language={language} />
                {r.status === 'pending' && (
                  <div className="flex gap-2 pt-2 border-t">
                    {rejectingId === r.id ? (
                      <div className="flex gap-2 w-full">
                        <Input
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder={t ? 'Raison du refus...' : 'Reason for rejection...'}
                          className="flex-1"
                        />
                        <Button size="sm" variant="destructive" onClick={() => handleReject(r.id)} disabled={rejectMutation.isPending}>
                          {t ? 'Confirmer' : 'Confirm'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRejectingId(null)}>
                          {t ? 'Annuler' : 'Cancel'}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleAccept(r.id)} disabled={acceptMutation.isPending}>
                          <CheckCircle className="mr-1 h-4 w-4" />
                          {t ? 'Accepter' : 'Accept'}
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => setRejectingId(r.id)}>
                          <XCircle className="mr-1 h-4 w-4" />
                          {t ? 'Refuser' : 'Reject'}
                        </Button>
                      </>
                    )}
                  </div>
                )}
                {r.status === 'accepted' && r.meetingLink && (
                  <a href={r.meetingLink} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="gap-1">
                      <ExternalLink className="h-4 w-4" />
                      {t ? 'Rejoindre la réunion' : 'Join Meeting'}
                    </Button>
                  </a>
                )}
                {r.status === 'accepted' && (
                  <DealCommunicationHistory contactRequest={r} language={language} />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Seller/Staff view — shows incoming requests for a specific deal */
export function DealContactRequests({ dealId, language = 'en' }: { dealId: string; language?: string }) {
  const { data: requests = [], isLoading } = useContactRequestsByDeal(dealId);
  const acceptMutation = useAcceptContactRequest();
  const rejectMutation = useRejectContactRequest();
  const { toast } = useToast();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const t = language === 'fr';

  const handleAccept = async (id: string) => {
    try {
      await acceptMutation.mutateAsync(id);
      toast({ title: t ? 'Demande acceptée' : 'Request accepted' });
    } catch {
      toast({ title: t ? 'Erreur' : 'Error', variant: 'destructive' });
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectMutation.mutateAsync({ id, reason: rejectReason });
      setRejectingId(null);
      setRejectReason('');
      toast({ title: t ? 'Demande refusée' : 'Request rejected' });
    } catch {
      toast({ title: t ? 'Erreur' : 'Error', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="h-5 w-5" />
          {t ? 'Demandes de contact' : 'Contact Requests'}
          {requests.filter(r => r.status === 'pending').length > 0 && (
            <Badge variant="destructive" className="ml-2">{requests.filter(r => r.status === 'pending').length}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : requests.length === 0 ? (
          <p className="text-center text-muted-foreground py-6">{t ? 'Aucune demande reçue' : 'No requests received'}</p>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="border rounded-lg p-4 space-y-3">
                <RequestRow request={r} language={language} />
                {r.status === 'pending' && (
                  <div className="flex gap-2 pt-2 border-t">
                    {rejectingId === r.id ? (
                      <div className="flex gap-2 w-full">
                        <Input
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder={t ? 'Raison du refus...' : 'Reason for rejection...'}
                          className="flex-1"
                        />
                        <Button size="sm" variant="destructive" onClick={() => handleReject(r.id)} disabled={rejectMutation.isPending}>
                          {t ? 'Confirmer' : 'Confirm'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRejectingId(null)}>
                          {t ? 'Annuler' : 'Cancel'}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleAccept(r.id)} disabled={acceptMutation.isPending}>
                          <CheckCircle className="mr-1 h-4 w-4" />
                          {t ? 'Accepter' : 'Accept'}
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => setRejectingId(r.id)}>
                          <XCircle className="mr-1 h-4 w-4" />
                          {t ? 'Refuser' : 'Reject'}
                        </Button>
                      </>
                    )}
                  </div>
                )}
                {r.status === 'accepted' && r.meetingLink && (
                  <a href={r.meetingLink} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="gap-1">
                      <ExternalLink className="h-4 w-4" />
                      {t ? 'Rejoindre la réunion' : 'Join Meeting'}
                    </Button>
                  </a>
                )}
                {r.status === 'accepted' && (
                  <DealCommunicationHistory contactRequest={r} language={language} />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Admin/Staff view — shows ALL contact requests across the platform */
export function AdminContactRequests({ language = 'en' }: { language?: string }) {
  const { data: requests = [], isLoading } = useAllContactRequests();
  const acceptMutation = useAcceptContactRequest();
  const rejectMutation = useRejectContactRequest();
  const { toast } = useToast();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('all');
  const t = language === 'fr';

  const handleAccept = async (id: string) => {
    try {
      await acceptMutation.mutateAsync(id);
      toast({ title: t ? 'Demande acceptée' : 'Request accepted' });
    } catch {
      toast({ title: t ? 'Erreur' : 'Error', variant: 'destructive' });
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectMutation.mutateAsync({ id, reason: rejectReason });
      setRejectingId(null);
      setRejectReason('');
      toast({ title: t ? 'Demande refusée' : 'Request rejected' });
    } catch {
      toast({ title: t ? 'Erreur' : 'Error', variant: 'destructive' });
    }
  };

  const filtered = statusFilter === 'all' ? requests : requests.filter(r => r.status === statusFilter);
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const acceptedCount = requests.filter(r => r.status === 'accepted').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-white">{requests.length}</p>
            <p className="text-xs text-gray-400">{t ? 'Total' : 'Total'}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-500">{pendingCount}</p>
            <p className="text-xs text-gray-400">{t ? 'En attente' : 'Pending'}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-500">{acceptedCount}</p>
            <p className="text-xs text-gray-400">{t ? 'Acceptées' : 'Accepted'}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-500">{rejectedCount}</p>
            <p className="text-xs text-gray-400">{t ? 'Refusées' : 'Rejected'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter tabs */}
      <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
        <TabsList className="bg-gray-800/50 border border-gray-700">
          <TabsTrigger value="all" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-gray-400">
            {t ? 'Toutes' : 'All'} ({requests.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-gray-400">
            {t ? 'En attente' : 'Pending'} ({pendingCount})
          </TabsTrigger>
          <TabsTrigger value="accepted" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-gray-400">
            {t ? 'Acceptées' : 'Accepted'} ({acceptedCount})
          </TabsTrigger>
          <TabsTrigger value="rejected" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-gray-400">
            {t ? 'Refusées' : 'Rejected'} ({rejectedCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Requests list */}
      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-8 text-center">
            <Users className="h-10 w-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">{t ? 'Aucune demande de contact' : 'No contact requests'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <Card key={r.id} className="bg-gray-800/50 border-gray-700">
              <CardContent className="p-4 space-y-3">
                <RequestRow request={r} language={language} />
                {r.status === 'pending' && (
                  <div className="flex gap-2 pt-2 border-t border-gray-700">
                    {rejectingId === r.id ? (
                      <div className="flex gap-2 w-full">
                        <Input
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder={t ? 'Raison du refus...' : 'Reason for rejection...'}
                          className="flex-1 bg-gray-900 border-gray-700 text-white"
                        />
                        <Button size="sm" variant="destructive" onClick={() => handleReject(r.id)} disabled={rejectMutation.isPending}>
                          {t ? 'Confirmer' : 'Confirm'}
                        </Button>
                        <Button size="sm" variant="ghost" className="text-gray-400" onClick={() => setRejectingId(null)}>
                          {t ? 'Annuler' : 'Cancel'}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleAccept(r.id)} disabled={acceptMutation.isPending}>
                          <CheckCircle className="mr-1 h-4 w-4" />
                          {t ? 'Accepter' : 'Accept'}
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => setRejectingId(r.id)}>
                          <XCircle className="mr-1 h-4 w-4" />
                          {t ? 'Refuser' : 'Reject'}
                        </Button>
                      </>
                    )}
                  </div>
                )}
                {r.status === 'accepted' && r.meetingLink && (
                  <a href={r.meetingLink} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="gap-1 border-gray-600 text-gray-300">
                      <ExternalLink className="h-4 w-4" />
                      {t ? 'Rejoindre la réunion' : 'Join Meeting'}
                    </Button>
                  </a>
                )}
                {r.status === 'accepted' && (
                  <DealCommunicationHistory contactRequest={r} language={language} />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function RequestRow({ request: r, language = 'en' }: { request: ContactRequest; language?: string }) {
  const cfg = STATUS_CONFIG[r.status];
  const Icon = cfg.icon;
  const t = language === 'fr';

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-1 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{r.dealName || r.dealId}</span>
          <Badge variant={cfg.variant} className="text-xs">
            <Icon className={`mr-1 h-3 w-3 ${cfg.color}`} />
            {t ? cfg.labelFr : cfg.label}
          </Badge>
        </div>
        {r.buyerName && <p className="text-xs text-muted-foreground">{t ? 'Acheteur' : 'Buyer'}: {r.buyerName}</p>}
        {r.buyerEmail && <p className="text-xs text-muted-foreground">{r.buyerEmail}</p>}
        {r.message && <p className="text-sm text-muted-foreground line-clamp-2">{r.message}</p>}
        {r.rejectionReason && <p className="text-xs text-red-500">{t ? 'Raison' : 'Reason'}: {r.rejectionReason}</p>}
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        {new Date(r.createdAt).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US')}
      </span>
    </div>
  );
}
